import React, {useState} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';

interface ExecutionAction {
  action: string;
  action_type?: string; // Backend may send action_type instead of action
  action_id: string;
  requirement_id?: string | null;
  element_id: string | null;
  element_name?: string | null;
  element_type: string | null;
  file: string | null;
  target_file?: string | null; // Backend may send target_file instead of file
  files_affected?: string[];
  description: string;
  instructions?: string;
  status: 'completed' | 'failed';
  error: string | null;
  duration_seconds: number;
  form_id?: string;
  formId?: string; // camelCase variant
  form_name?: string;
  formName?: string; // camelCase variant
  text_id?: string;
  xml_before: string | null;
  xml_after: string | null;
}

interface ExecutionSummary {
  trial_id: string;
  session_id: string;
  platform: string;
  status: string;
  total_actions: number;
  successful_actions: number;
  failed_actions: number;
  duration_seconds: number;
  executed_at: string;
  actions: ExecutionAction[];
}

interface ExecutionSummaryTableProps {
  summary: ExecutionSummary;
  onViewDiff: (action: ExecutionAction) => void;
}

// ============================================
// ACTION ROW (expandable, matching Flutter)
// ============================================

function ActionResultRow({
  action,
  onViewDiff,
}: {
  action: ExecutionAction;
  onViewDiff: (action: ExecutionAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const isSuccess = action.status === 'completed';

  // Resolve action type: backend may send action_type or action
  const resolvedAction = action.action_type || action.action || '';
  // Resolve file: backend may send files_affected array, target_file, or file
  const resolvedFile =
    action.file ||
    action.target_file ||
    (action.files_affected && action.files_affected.length > 0
      ? action.files_affected[0]
      : null);

  const getActionTypeLabel = (): string => {
    if (resolvedAction === 'download_gl') return 'DOWNLOAD GL';
    if (resolvedAction === 'create_custom_form') return 'CREATE FORM';
    if (resolvedAction.startsWith('add_')) return 'ADD';
    if (resolvedAction.startsWith('modify_')) return 'MODIFY';
    if (resolvedAction.startsWith('remove_')) return 'REMOVE';
    return resolvedAction.toUpperCase().replace(/_/g, ' ') || '-';
  };

  const getElementDisplay = (): {primary: string; secondary?: string} => {
    if (resolvedAction === 'download_gl' || resolvedAction === 'create_custom_form') {
      const formId = action.form_id || action.formId || '-';
      const formName = action.form_name || action.formName;
      return {primary: formId, secondary: formName};
    }
    if (
      ['add_variable', 'add_condition', 'add_action', 'modify_element'].includes(
        resolvedAction
      )
    ) {
      return {
        primary: action.element_type || '-',
        secondary: action.element_id || undefined,
      };
    }
    if (resolvedAction === 'add_text') {
      return {primary: action.text_id || '-'};
    }
    // Show element name + ID if available
    if (action.element_name) {
      return {primary: action.element_name, secondary: action.element_id || undefined};
    }
    return {primary: action.element_id || '-'};
  };

  const getActionColor = (): 'error' | 'success' | 'info' | 'primary' => {
    if (resolvedAction.startsWith('remove_') || resolvedAction.startsWith('delete_'))
      return 'error';
    if (resolvedAction.startsWith('add_') || resolvedAction === 'create_custom_form')
      return 'success';
    if (resolvedAction === 'download_gl') return 'primary';
    return 'info';
  };

  const elementDisplay = getElementDisplay();
  const hasDiff = !!(action.xml_before || action.xml_after);
  const actionLabel = getActionTypeLabel();

  return (
    <>
      <TableRow
        sx={{
          '& > *': {borderBottom: 'unset'},
          cursor: 'pointer',
          '&:hover': {bgcolor: 'action.hover'},
          '& .MuiTableCell-root': {py: 1.5},
          ...(
            !isSuccess && {
              bgcolor: (theme: any) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(211,47,47,0.06)'
                  : 'rgba(211,47,47,0.03)',
            }
          ),
        }}
        onClick={() => setOpen(!open)}
      >
        {/* Expand icon */}
        <TableCell sx={{width: 40, px: 1}}>
          <IconButton size="small">
            {open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>

        {/* Status */}
        <TableCell sx={{width: 110, maxWidth: 110}}>
          <Tooltip title={isSuccess ? 'Completed successfully' : action.error || 'Failed'}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 3,
                border: '1px solid',
                bgcolor: (theme: any) =>
                  isSuccess
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(46,125,50,0.12)'
                      : 'rgba(46,125,50,0.08)'
                    : theme.palette.mode === 'dark'
                      ? 'rgba(211,47,47,0.12)'
                      : 'rgba(211,47,47,0.08)',
                borderColor: (theme: any) =>
                  isSuccess
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(46,125,50,0.3)'
                      : 'rgba(46,125,50,0.35)'
                    : theme.palette.mode === 'dark'
                      ? 'rgba(211,47,47,0.3)'
                      : 'rgba(211,47,47,0.35)',
              }}
            >
              {isSuccess ? (
                <CheckCircleIcon sx={{fontSize: 14, color: 'success.main'}} />
              ) : (
                <ErrorIcon sx={{fontSize: 14, color: 'error.main'}} />
              )}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: isSuccess ? 'success.main' : 'error.main',
                }}
              >
                {isSuccess ? 'Success' : 'Failed'}
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>

        {/* Element */}
        <TableCell sx={{width: 280, maxWidth: 280}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
            <Typography 
              variant="body2" 
              sx={{
                fontWeight: 500, 
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal'
              }}
            >
              {action.element_name || elementDisplay.primary}
            </Typography>
            {(action.element_name ? action.element_id : elementDisplay.secondary) && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal'
                }}
              >
                ({action.element_name ? action.element_id : elementDisplay.secondary})
              </Typography>
            )}
          </Box>
        </TableCell>

        {/* Action Type */}
        <TableCell sx={{width: 140, maxWidth: 140}}>
          {actionLabel && actionLabel !== '-' ? (
            <Chip
              label={actionLabel}
              color={getActionColor()}
              size="small"
              sx={{fontSize: '0.7rem', height: 24, fontWeight: 500}}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* Requirement */}
        <TableCell sx={{width: 120, maxWidth: 120}}>
          {action.requirement_id ? (
            <Chip
              label={action.requirement_id}
              size="small"
              variant="outlined"
              color="primary"
              sx={{fontSize: '0.7rem', height: 24, fontWeight: 600}}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </TableCell>

        {/* File */}
        <TableCell sx={{width: 320, maxWidth: 320}}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal'
            }}
          >
            {resolvedFile || '-'}
          </Typography>
        </TableCell>

        {/* Duration */}
        <TableCell sx={{width: 85, maxWidth: 85}}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{fontFamily: 'monospace', fontSize: '0.8rem'}}
          >
            {formatDuration(action.duration_seconds)}
          </Typography>
        </TableCell>

        {/* Actions */}
        <TableCell align="center" sx={{width: 70, maxWidth: 70}}>
          <Tooltip title={hasDiff ? 'View Diff' : 'No diff available'}>
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!hasDiff}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDiff(action);
                }}
              >
                <VisibilityIcon sx={{fontSize: 18}} />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Expanded details */}
      <TableRow>
        <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                mx: 2,
                my: 2,
                p: 2.5,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              {/* Detail fields as a clean inline row */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                {resolvedAction && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                    >
                      Action
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8rem'}}>
                      {resolvedAction.replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                )}
                {action.element_type && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                    >
                      Element Type
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8rem'}}>
                      {action.element_type}
                    </Typography>
                  </Box>
                )}
                {action.element_id && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                    >
                      Element ID
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{
                        fontWeight: 600, 
                        fontSize: '0.8rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {action.element_id}
                    </Typography>
                  </Box>
                )}
                {action.requirement_id && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                    >
                      Requirement
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8rem', color: 'primary.main'}}>
                      {action.requirement_id}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                  >
                    Duration
                  </Typography>
                  <Typography variant="body2" sx={{fontWeight: 600, fontSize: '0.8rem', fontFamily: 'monospace'}}>
                    {formatDuration(action.duration_seconds)}
                  </Typography>
                </Box>
              </Box>

              {/* Second row for element name and file */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  mt: 2,
                  alignItems: 'start',
                }}
              >
                {action.element_name && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                    >
                      Element Name
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{
                        fontWeight: 600, 
                        fontSize: '0.8rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}
                    >
                      {action.element_name}
                    </Typography>
                  </Box>
                )}
                {resolvedFile && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{display: 'block', mb: 0.25, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                    >
                      File
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{
                        fontWeight: 600, 
                        fontSize: '0.8rem', 
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}
                    >
                      {resolvedFile}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Description */}
              {action.description && (
                <Box sx={{mt: 2, pt: 2, borderTop: 1, borderColor: 'divider'}}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                  >
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{fontSize: '0.8rem'}}>
                    {action.description}
                  </Typography>
                </Box>
              )}

              {/* Instructions */}
              {action.instructions && (
                <Box sx={{mt: 2, pt: 2, borderTop: 1, borderColor: 'divider'}}>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{display: 'block', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5}}
                  >
                    Instructions
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        m: 0,
                      }}
                    >
                      {action.instructions}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Error details */}
              {action.error && (
                <Box sx={{mt: 2, pt: 2, borderTop: 1, borderColor: 'divider'}}>
                  <Typography
                    variant="caption"
                    sx={{display: 'block', mb: 0.75, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'error.main'}}
                  >
                    Error
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(211,47,47,0.08)'
                          : 'rgba(211,47,47,0.04)',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'error.light',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{color: 'error.main', fontFamily: 'monospace', fontSize: '0.78rem'}}
                    >
                      {action.error}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
}

// ============================================
// MAIN COMPONENT
// ============================================

export const ExecutionSummaryTable: React.FC<ExecutionSummaryTableProps> = ({
  summary,
  onViewDiff,
}) => {
  // Filter out note and unresolved actions - they are not executable
  // Backend may send action_type or action
  const getResolvedAction = (a: ExecutionAction) => a.action_type || a.action || '';
  const executableActions = summary.actions.filter(
    (a) => getResolvedAction(a) !== 'note' && getResolvedAction(a) !== 'unresolved'
  );
  const skippedNotes = summary.actions.filter(
    (a) => getResolvedAction(a) === 'note'
  ).length;
  const skippedUnresolved = summary.actions.filter(
    (a) => getResolvedAction(a) === 'unresolved'
  ).length;

  // Recalculate counts based on executable actions only
  const actualTotal = executableActions.length;
  const actualSuccessful = executableActions.filter(
    (a) => a.status === 'completed'
  ).length;
  const actualFailed = executableActions.filter(
    (a) => a.status === 'failed'
  ).length;

  return (
    <Box>
      {/* Summary Header (matching Flutter: inline title + stats in a single row) */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: 'action.hover',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
          Execution Summary
        </Typography>

        {/* Stat boxes (inline, compact) */}
        <Box sx={{display: 'flex', gap: 3, alignItems: 'center'}}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.2}}>
              Total Actions
            </Typography>
            <Typography variant="h6" sx={{fontWeight: 700, lineHeight: 1.3}}>
              {actualTotal}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.2}}>
              Successful
            </Typography>
            <Typography variant="h6" color="success.main" sx={{fontWeight: 700, lineHeight: 1.3}}>
              {actualSuccessful}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.2}}>
              Failed
            </Typography>
            <Typography variant="h6" color="error.main" sx={{fontWeight: 700, lineHeight: 1.3}}>
              {actualFailed}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.2}}>
              Duration
            </Typography>
            <Typography variant="h6" sx={{fontWeight: 700, lineHeight: 1.3}}>
              {formatDuration(summary.duration_seconds)}
            </Typography>
          </Box>

          {/* Skipped info */}
          {(skippedNotes > 0 || skippedUnresolved > 0) && (
            <Box
              sx={{
                borderLeft: 1,
                borderColor: 'divider',
                pl: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Skipped:
              </Typography>
              {skippedNotes > 0 && (
                <Chip
                  label={`${skippedNotes} note${skippedNotes > 1 ? 's' : ''}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{fontSize: '0.7rem', height: 22}}
                />
              )}
              {skippedUnresolved > 0 && (
                <Chip
                  label={`${skippedUnresolved} unresolved`}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{fontSize: '0.7rem', height: 22}}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Actions Table */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <TableContainer
          sx={{
            overflowX: 'auto',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': { height: 12 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.200' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 6 },
            '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.500' },
          }}
        >
          <Table aria-label="execution actions table" sx={{minWidth: 800}}>
            <TableHead>
              <TableRow sx={{bgcolor: 'action.hover'}}>
                <TableCell sx={{width: 40, px: 1}} />
                <TableCell sx={{width: 110, maxWidth: 110, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>
                  Status
                </TableCell>
                <TableCell sx={{width: 280, maxWidth: 280, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>
                  Element
                </TableCell>
                <TableCell sx={{width: 140, maxWidth: 140, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>
                  Action Type
                </TableCell>
                <TableCell sx={{width: 120, maxWidth: 120, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>
                  Requirement
                </TableCell>
                <TableCell sx={{width: 320, maxWidth: 320, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}>
                  File
                </TableCell>
                <TableCell
                  sx={{
                    width: 85,
                    maxWidth: 85,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    py: 1.5,
                  }}
                >
                  Duration
                </TableCell>
                <TableCell
                  align="center"
                  sx={{width: 70, maxWidth: 70, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1.5}}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executableActions.map((action, index) => (
                <ActionResultRow
                  key={index}
                  action={action}
                  onViewDiff={onViewDiff}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Footer caption */}
      <Box sx={{mt: 2.5, display: 'flex', gap: 2.5}}>
        <Typography variant="caption" color="text.secondary">
          {actualTotal} total actions
        </Typography>
        <Typography variant="caption" color="success.main" sx={{fontWeight: 500}}>
          {actualSuccessful} successful
        </Typography>
        {actualFailed > 0 && (
          <Typography variant="caption" color="error.main" sx={{fontWeight: 500}}>
            {actualFailed} failed
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled">
          {formatDuration(summary.duration_seconds)}
        </Typography>
      </Box>
    </Box>
  );
};
